/**
 * The world renderer.
 *
 * A single Three.js scene draws the whole visible city the way the game means
 * it to be seen: a bright daytime toy city from a low aerial camera, matched
 * to the Capital Rift reference — grass ground, grey roads with painted
 * dashes, sunlit facades dressed by one shared window texture, projected
 * ground shadows, trees on open land and cars ambling the streets. Chunk data
 * arrives as plain features (parcels, buildings, roads) and is merged into a
 * handful of meshes per chunk; ownership, overlays and selection are baked
 * into vertex colours and rebuilt only for the chunks they touch.
 *
 * The renderer owns the camera, input, picking and the HTML label overlay.
 * React never renders into the canvas — it calls the setters below when store
 * state changes, and receives picks and idle notifications through callbacks.
 */

import {
  BoxGeometry,
  BufferAttribute,
  BufferGeometry,
  CanvasTexture,
  Color,
  CylinderGeometry,
  DoubleSide,
  FogExp2,
  Group,
  InstancedMesh,
  LineBasicMaterial,
  LineSegments,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  PerspectiveCamera,
  PlaneGeometry,
  Line,
  Quaternion,
  Raycaster,
  RepeatWrapping,
  RingGeometry,
  Scene,
  SphereGeometry,
  SRGBColorSpace,
  Texture,
  Vector2,
  Vector3,
  WebGLRenderer,
} from "three";
import type {
  AdminAreasResponse,
  FloorPlanResponse,
  MapLabelFeature,
  WorldTileResponse,
} from "@parcelbound/contracts";
import { buildAdminAreas } from "./adminmap";
import {
  ARRIVE_EPS_M,
  CAMERA_FOLLOW,
  PATH_COLOR,
  PLAYER_BODY_COLOR,
  PLAYER_HEAD_COLOR,
  PLAYER_RING_COLOR,
  PLAYER_VISIBLE_DIST,
  TARGET_RING_RADIUS_M,
  WALK_SPEED_MPS,
} from "./config";
import { busynessAt } from "./overlay";
import { ResourceFieldLayer } from "./resourcefield";
import {
  dedupedRing,
  extrudeWalls,
  fillPolygon,
  flatRoof,
  gableRoof,
  laneDashes,
  LineSink,
  makeProjection,
  MeshSink,
  projectedShadow,
  ribbon,
  tree,
  type Projection,
  type TriRange,
  type Vec2,
} from "./geometry";
import * as P from "./palette";

export interface RenderCtx {
  myId: string | null;
  selectedId: string | null;
  mapLayer: "world" | "foot_traffic" | "logistics";
  showResources: boolean;
  /**
   * Building shown as an interior cutaway: everything above the selected
   * floor comes off, the storeys below keep their windowed exterior.
   */
  cutawayBuildingId: string | null;
  /** Metres from the ground to the selected floor's slab. */
  cutawayBaseM: number;
}

export interface LabelSpec {
  lngLat: Vec2;
  /** Metres above ground, so room pills float at their floor. */
  altitude?: number;
  el: HTMLElement;
}

export interface WorldRendererOptions {
  container: HTMLElement;
  labelLayer: HTMLElement;
  fpsNode?: HTMLElement | null;
  home: Vec2;
  onPickParcel: (id: string) => void;
  onPickRoom: (id: string) => void;
  /** Camera has settled somewhere new; fetch what it can see. */
  onIdle: () => void;
}

const MIN_DIST = 140;
/** Far enough back to frame the whole planet on one screen. */
const MAX_DIST = 26_000_000;
/** Beyond this the 3D city hands over to the world map. */
export const MAP_VIEW_DIST = 9_000;
/** Start loading the atlas a little before the handover so it never pops in late. */
const MAP_PRELOAD_DIST = 5_000;
/** Haze is at full strength below this camera distance… */
const FOG_FULL_DIST = 2_500;
/** …and fully gone by this one, well before the map takes over. */
const FOG_CLEAR_DIST = 8_000;
const FOG_DENSITY = 0.00013;
/** The sky fades to space across this band of camera distance. */
const SPACE_NEAR_DIST = 3_000_000;
const SPACE_FAR_DIST = 20_000_000;
/** Continent names take over from country names past this. */
const CONTINENT_DIST = 9_000_000;
/** Below this the detailed admin layer replaces the coarse bundled atlas,
 * and region/city labels join the ladder. */
const DETAIL_MAP_DIST = 2_500_000;
/** Country labels drop out below this — regions and cities carry the map. */
const COUNTRY_LABEL_MIN_DIST = 250_000;

type MapPlaceKind = "country" | "continent";
/** Gameplay data is only worth fetching below this camera distance. */
export const FETCH_DIST = 4_200;
/** Labels are only readable below this. */
export const LABEL_DIST = 3_000;
const IDLE_MS = 350;
/** Ground-plane span cap for chunk requests, in degrees (≈ old MAX_SPAN). */
const MAX_SPAN_DEG = 0.07;

const TILT_LOW = 0.96; // ~55° above horizon: the game view
const TILT_TOP = 1.45; // near top-down

/** Stack of flat layers above the ground plane, lowest first. */
const Y = {
  plot: 0.12,
  casing: 0.3,
  road: 0.45,
  paint: 0.55,
  shadow: 0.62,
  accent: 0.75,
  outline: 0.85,
} as const;

/** The grass texture tiles this many metres; the ground snaps to it. */
const GRASS_TILE = 840;
const GROUND_SPAN = 160_000;

interface Car {
  mesh: Mesh;
  /** Projected centre-line, right-lane offset applied, with running length. */
  pts: Array<[number, number]>;
  cum: number[];
  total: number;
  speed: number;
  phase: number;
  reverse: boolean;
}

/** One walker on a sidewalk path; drawn via the chunk's instanced meshes. */
interface Walker {
  pts: Array<[number, number]>;
  cum: number[];
  total: number;
  speed: number;
  phase: number;
  scale: number;
}

interface PedCrowd {
  bodies: InstancedMesh;
  heads: InstancedMesh;
  walkers: Walker[];
}

interface ChunkMeshes {
  group: Group;
  chunk: WorldTileResponse;
  ctxKey: string;
  parcelRanges: TriRange[];
  parcelMesh: Mesh | null;
  buildingRanges: TriRange[];
  buildingMesh: Mesh | null;
  roofRanges: TriRange[];
  roofMesh: Mesh | null;
  cars: Car[];
  peds: PedCrowd | null;
}

/** Shared across chunks; never disposed with them (userData.shared guards). */
const carGeometry = new BoxGeometry(4.2, 1.5, 2.0);
const carMaterials = P.CAR_COLORS.map((c) => new MeshBasicMaterial({ color: c }));
const pedBodyGeometry = new CylinderGeometry(0.32, 0.4, 1.15, 6);
const pedHeadGeometry = new SphereGeometry(0.3, 8, 6);
const pedBodyMaterial = new MeshBasicMaterial(); // tinted per instance
const pedHeadMaterial = new MeshBasicMaterial({ color: P.PED_HEAD });

export class WorldRenderer {
  private readonly opts: WorldRendererOptions;
  private readonly renderer: WebGLRenderer;
  private readonly scene = new Scene();
  private readonly camera: PerspectiveCamera;
  private readonly raycaster = new Raycaster();
  readonly proj: Projection;

  /** Orbit state. `desired*` are eased towards each frame. */
  private target = new Vector3();
  private desiredTarget = new Vector3();
  private dist = 1_600;
  private desiredDist = 1_600;
  private azimuth = 0.35;
  private desiredAzimuth = 0.35;
  private elevation = TILT_LOW;
  private desiredElevation = TILT_LOW;

  /** One persistent fog whose density follows the camera distance. */
  private readonly fog = new FogExp2(P.HAZE, FOG_DENSITY);
  private readonly skyColor = new Color(P.SKY);
  private readonly spaceColor = new Color(P.SPACE);

  private readonly chunkMeshes = new Map<string, ChunkMeshes>();
  private readonly worldGroup = new Group();
  private readonly laneGroup = new Group();
  private readonly roomGroup = new Group();
  private roomRanges: TriRange[] = [];
  private roomMesh: Mesh | null = null;
  private ground!: Mesh;
  private readonly windowTexture: Texture;
  private readonly grassTexture: Texture;
  private readonly grassMaterial: MeshBasicMaterial;
  private readonly oceanMaterial: MeshBasicMaterial;

  /** Far-zoom world map. Lazily built the first time someone zooms out. */
  private readonly mapGroup = new Group();
  private readonly overviewGroup = new Group();
  /** Detailed admin-area layer (10m coastlines, region borders) fetched from
   * the API; supersedes the coarse bundled atlas below continental range. */
  private readonly detailMapGroup = new Group();
  private atlasGroup: Group | null = null;
  private detailLoaded = false;
  private mapPlaces: Array<{ pos: Vector3; el: HTMLElement; kind: MapPlaceKind }> = [];
  private adminPlaces: Array<{ pos: Vector3; el: HTMLElement; sizeM: number }> = [];
  private cityPlaces: Array<{ pos: Vector3; el: HTMLElement; rank: number }> = [];
  private mapMarkers: Array<{ pos: Vector3; el: HTMLElement }> = [];
  private mapBuildStarted = false;
  private mapView = false;
  /** The resources heatmap, created the first time the overlay opens. */
  private resourceLayer: ResourceFieldLayer | null = null;
  private showResources = false;

  /** The player's in-world avatar. Client-side only for now. */
  private readonly playerGroup = new Group();
  private readonly playerPos = new Vector3();
  private walkTarget: Vector3 | null = null;
  private followPlayer = false;
  private pathLine!: Line;
  private targetRing!: Mesh;
  private couriers: Array<{ mesh: Mesh; from: Vector3; to: Vector3; period: number; phase: number }> = [];
  private labels: Array<{ pos: Vector3; el: HTMLElement }> = [];

  private raf = 0;
  private lastTime = 0;
  private fpsAccum = 0;
  private fpsFrames = 0;
  private idleTimer: number | null = null;
  private moved = true;
  private disposed = false;
  private readonly resizeObserver: ResizeObserver;
  private readonly detachInput: () => void;

  constructor(opts: WorldRendererOptions) {
    this.opts = opts;
    this.proj = makeProjection(opts.home);

    this.renderer = new WebGLRenderer({
      antialias: true,
      logarithmicDepthBuffer: true,
    });
    this.renderer.setPixelRatio(Math.min(2, window.devicePixelRatio));
    this.renderer.domElement.style.display = "block";
    opts.container.appendChild(this.renderer.domElement);

    this.camera = new PerspectiveCamera(52, 1, 4, 60_000);
    this.scene.background = new Color(P.SKY);
    this.scene.fog = this.fog;
    this.scene.add(this.worldGroup, this.laneGroup, this.roomGroup);

    const maxAniso = this.renderer.capabilities.getMaxAnisotropy();
    this.windowTexture = makeWindowTexture(Math.min(8, maxAniso));
    this.grassTexture = makeGrassTexture(Math.min(8, maxAniso));

    // The ground: one huge grass plane. It follows the camera target (see
    // frame()) because the world is global and a fixed plane has an edge
    // somewhere — snapping to whole texture tiles keeps the blotches still.
    // In map view the same plane becomes the ocean and scales up with the
    // camera so the planet always sits on water.
    this.grassTexture.repeat.set(GROUND_SPAN / GRASS_TILE, GROUND_SPAN / GRASS_TILE);
    this.grassMaterial = new MeshBasicMaterial({ map: this.grassTexture });
    this.oceanMaterial = new MeshBasicMaterial({ color: P.OCEAN });
    this.ground = new Mesh(new PlaneGeometry(GROUND_SPAN, GROUND_SPAN), this.grassMaterial);
    this.ground.rotation.x = -Math.PI / 2;
    this.ground.position.y = -0.5;
    this.scene.add(this.ground);

    this.mapGroup.visible = false;
    this.overviewGroup.visible = false;
    this.detailMapGroup.visible = false;
    this.mapGroup.add(this.overviewGroup, this.detailMapGroup);
    this.scene.add(this.mapGroup);

    this.buildPlayer();

    this.resize();
    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(opts.container);
    this.detachInput = this.attachInput();

    this.lastTime = performance.now();
    const loop = (now: number): void => {
      if (this.disposed) return;
      this.frame(now);
      this.raf = requestAnimationFrame(loop);
    };
    this.raf = requestAnimationFrame(loop);
  }

  dispose(): void {
    this.disposed = true;
    cancelAnimationFrame(this.raf);
    if (this.idleTimer !== null) window.clearTimeout(this.idleTimer);
    this.resizeObserver.disconnect();
    this.detachInput();
    for (const entry of this.chunkMeshes.values()) disposeGroup(entry.group);
    disposeGroup(this.laneGroup);
    disposeGroup(this.roomGroup);
    disposeGroup(this.mapGroup);
    for (const p of this.mapPlaces) p.el.remove();
    for (const p of this.adminPlaces) p.el.remove();
    for (const p of this.cityPlaces) p.el.remove();
    for (const m of this.mapMarkers) m.el.remove();
    disposeGroup(this.playerGroup);
    this.pathLine.geometry.dispose();
    (this.pathLine.material as LineBasicMaterial).dispose();
    this.targetRing.geometry.dispose();
    (this.targetRing.material as MeshBasicMaterial).dispose();
    this.resourceLayer?.dispose();
    this.windowTexture.dispose();
    this.grassTexture.dispose();
    this.renderer.dispose();
    this.renderer.domElement.remove();
  }

  /* ---------------- camera ---------------- */

  cameraLngLat(): Vec2 {
    return this.proj.toLngLat(this.target.x, this.target.z);
  }

  targetDistance(): number {
    return this.desiredDist;
  }

  flyTo(lngLat: Vec2, dist?: number): void {
    const [x, z] = this.proj.toWorld(lngLat);
    this.desiredTarget.set(x, 0, z);
    if (dist !== undefined) this.desiredDist = clamp(dist, MIN_DIST, MAX_DIST);
    this.moved = true;
    // An explicit fly-to takes the camera away from the walking avatar.
    this.followPlayer = false;
  }

  /* ---------------- player avatar ---------------- */

  /** The avatar: pedestrian proportions scaled up, plus a white ground ring. */
  private buildPlayer(): void {
    const body = new Mesh(
      new CylinderGeometry(0.6, 0.75, 2.1, 10),
      new MeshBasicMaterial({ color: PLAYER_BODY_COLOR }),
    );
    body.position.y = 1.15;
    const head = new Mesh(
      new SphereGeometry(0.55, 12, 10),
      new MeshBasicMaterial({ color: PLAYER_HEAD_COLOR }),
    );
    head.position.y = 2.75;
    const ring = new Mesh(
      new RingGeometry(0.95, 1.35, 24),
      new MeshBasicMaterial({ color: PLAYER_RING_COLOR, transparent: true, opacity: 0.85 }),
    );
    ring.rotation.x = -Math.PI / 2;
    ring.position.y = 0.95; // above accents/outlines, below labels
    this.playerGroup.add(body, head, ring);

    // Walk indicator: a green line to the destination plus a pulsing ring.
    const lineGeom = new BufferGeometry();
    lineGeom.setAttribute("position", new BufferAttribute(new Float32Array(6), 3));
    this.pathLine = new Line(
      lineGeom,
      new LineBasicMaterial({ color: PATH_COLOR, transparent: true, opacity: 0.9 }),
    );
    this.pathLine.visible = false;
    this.pathLine.frustumCulled = false;
    this.targetRing = new Mesh(
      new RingGeometry(TARGET_RING_RADIUS_M * 0.55, TARGET_RING_RADIUS_M, 28),
      new MeshBasicMaterial({ color: PATH_COLOR, transparent: true, opacity: 0.9 }),
    );
    this.targetRing.rotation.x = -Math.PI / 2;
    this.targetRing.visible = false;

    // Spawn at home. Direct children of the scene: chunk lifecycle and the
    // map-mode group swaps must never touch the player.
    this.playerPos.set(0, 0, 0);
    this.playerGroup.position.copy(this.playerPos);
    this.scene.add(this.playerGroup, this.pathLine, this.targetRing);
  }

  /** Send the avatar walking to a metre-space ground point. */
  walkTo(at: Vector3): void {
    this.walkTarget = new Vector3(at.x, 0, at.z);
    this.targetRing.position.set(at.x, 0.9, at.z);
    if (CAMERA_FOLLOW) this.followPlayer = true;
  }

  /** Recentre on the avatar and resume following it. */
  focusPlayer(): void {
    this.desiredTarget.copy(this.playerPos);
    if (this.desiredDist > 1_200) this.desiredDist = 600;
    this.followPlayer = true;
    this.moved = true;
  }

  playerLngLat(): Vec2 {
    return this.proj.toLngLat(this.playerPos.x, this.playerPos.z);
  }

  /**
   * Frame a building for the docked property card: the camera target sits to
   * the screen-right of the building, so the building itself lands in the
   * clear left of the viewport instead of behind the card — otherwise its
   * own room markers are unclickable.
   */
  frameBuilding(lngLat: Vec2, dist?: number): void {
    const [x, z] = this.proj.toWorld(lngLat);
    const d = clamp(dist ?? this.desiredDist, MIN_DIST, MAX_DIST);
    // Screen-right in world space for the current bearing.
    const rightX = Math.cos(this.desiredAzimuth);
    const rightZ = -Math.sin(this.desiredAzimuth);
    const shift = d * 0.3;
    this.desiredTarget.set(x + rightX * shift, 0, z + rightZ * shift);
    this.desiredDist = d;
    this.moved = true;
  }

  zoomBy(factor: number): void {
    this.desiredDist = clamp(this.desiredDist / factor, MIN_DIST, MAX_DIST);
    this.moved = true;
  }

  toggleTilt(): void {
    this.desiredElevation = this.desiredElevation > (TILT_LOW + TILT_TOP) / 2 ? TILT_LOW : TILT_TOP;
    this.moved = true;
  }

  resetBearing(): void {
    // Unwind to the nearest "north up" so the camera takes the short way round.
    this.desiredAzimuth = Math.round(this.desiredAzimuth / (Math.PI * 2)) * Math.PI * 2;
    this.moved = true;
  }

  /**
   * Ground actually in view, for chunk requests. Sampled like the old
   * MapLibre path: bottom corners, upper corners, top centre — rays that
   * clear the horizon are simply skipped, and the span is capped because
   * near-horizontal rays land absurdly far away.
   */
  viewBounds(): { west: number; south: number; east: number; north: number } {
    const centre = this.cameraLngLat();
    let west = centre[0];
    let east = centre[0];
    let south = centre[1];
    let north = centre[1];
    for (const [sx, sy] of [
      [-1, -1],
      [1, -1],
      [-1, 0.3],
      [1, 0.3],
      [0, 1],
    ] as const) {
      const hit = this.groundAtScreen(sx, sy);
      if (!hit) continue;
      const [lon, lat] = this.proj.toLngLat(hit.x, hit.z);
      west = Math.min(west, lon);
      east = Math.max(east, lon);
      south = Math.min(south, lat);
      north = Math.max(north, lat);
    }
    if (east - west > MAX_SPAN_DEG) {
      west = centre[0] - MAX_SPAN_DEG / 2;
      east = centre[0] + MAX_SPAN_DEG / 2;
    }
    if (north - south > MAX_SPAN_DEG) {
      south = centre[1] - MAX_SPAN_DEG / 2;
      north = centre[1] + MAX_SPAN_DEG / 2;
    }
    return {
      west: Math.max(-180, west),
      south: Math.max(-85, south),
      east: Math.min(180, east),
      north: Math.min(85, north),
    };
  }

  /* ---------------- data ---------------- */

  setChunks(chunks: Record<string, WorldTileResponse>, ctx: RenderCtx): void {
    this.showResources = ctx.showResources;
    if (ctx.showResources && !this.resourceLayer) {
      this.resourceLayer = new ResourceFieldLayer();
      this.scene.add(this.resourceLayer.mesh);
    }

    // Drop meshes for chunks that vanished (sign-out wipes the store).
    for (const [id, entry] of this.chunkMeshes) {
      if (!chunks[id]) {
        this.worldGroup.remove(entry.group);
        disposeGroup(entry.group);
        this.chunkMeshes.delete(id);
      }
    }
    for (const [id, chunk] of Object.entries(chunks)) {
      const key = this.chunkCtxKey(chunk, ctx);
      const existing = this.chunkMeshes.get(id);
      if (existing && existing.chunk === chunk && existing.ctxKey === key) continue;
      if (existing) {
        this.worldGroup.remove(existing.group);
        disposeGroup(existing.group);
      }
      const built = this.buildChunk(chunk, ctx, key);
      this.chunkMeshes.set(id, built);
      this.worldGroup.add(built.group);
    }
  }

  /**
   * The parts of the render context this chunk actually depends on. Selecting
   * a parcel rebuilds the two chunks involved, not the whole city.
   */
  private chunkCtxKey(chunk: WorldTileResponse, ctx: RenderCtx): string {
    const hasSelection =
      ctx.selectedId !== null &&
      (chunk.parcels.some((p) => p.id === ctx.selectedId) ||
        chunk.buildings.some((b) => b.parcelId === ctx.selectedId));
    const hasCutaway =
      ctx.cutawayBuildingId !== null && chunk.buildings.some((b) => b.id === ctx.cutawayBuildingId);
    // showResources is deliberately absent: the overlay is its own layer now,
    // so toggling it must not rebuild every chunk.
    return [
      ctx.myId ?? "-",
      ctx.mapLayer,
      hasSelection ? ctx.selectedId : "-",
      hasCutaway ? `${ctx.cutawayBuildingId}@${ctx.cutawayBaseM.toFixed(1)}` : "-",
    ].join("|");
  }

  private buildChunk(chunk: WorldTileResponse, ctx: RenderCtx, ctxKey: string): ChunkMeshes {
    const group = new Group();
    const proj = this.proj;

    // Roads: pale sidewalk casing, grey surface, painted centre dashes. In
    // the foot-traffic view both surface and casing instead run a busyness
    // gradient — the sidewalks are the glow, per the reference.
    const traffic = ctx.mapLayer === "foot_traffic";
    const busySamples = traffic
      ? chunk.parcels.map((p) => {
          const [x, z] = proj.toWorld(p.centroid);
          return { x, z, busyness: p.busyness };
        })
      : [];
    const casing = new MeshSink();
    const surface = new MeshSink();
    const paint = new MeshSink();
    for (const r of chunk.roads ?? []) {
      const colour = P.ROAD_SURFACE[r.roadClass] ?? P.ROAD_SURFACE.service!;
      // A few centimetres of per-road lift: crossing roads would otherwise
      // draw coplanar surfaces at junctions and z-fight into smudges.
      const lift = P.hash01(r.id + "y") * 0.05;
      if (traffic) {
        const heat = r.line.map((p) => {
          const [x, z] = proj.toWorld(p);
          return Math.min(1, busynessAt(x, z, busySamples) / 3);
        });
        const surfAt = (i: number): number =>
          P.mixRgb(P.TRAFFIC_ROAD_QUIET, P.TRAFFIC_ROAD_GLOW, heat[i] ?? 0);
        const caseAt = (i: number): number => P.shade(surfAt(i), 1.25);
        ribbon(casing, r.line, proj, r.halfWidthM + 2.0, Y.casing + lift, 0, caseAt);
        ribbon(surface, r.line, proj, r.halfWidthM, Y.road + lift, 0, surfAt);
      } else {
        ribbon(casing, r.line, proj, r.halfWidthM + 2.0, Y.casing + lift, P.ROAD_CASING);
        ribbon(surface, r.line, proj, r.halfWidthM, Y.road + lift, colour);
      }
      if (r.halfWidthM >= 3) laneDashes(paint, r.line, proj, Y.paint + lift, P.LANE_PAINT);
    }
    addMesh(group, casing);
    addMesh(group, surface);
    addMesh(group, paint);

    // Parcels: hash-varied grass quilt always, accents and outlines from
    // context — gold wash for yours, mint outline for listings.
    const plots = new MeshSink();
    const accents = new MeshSink();
    const outlines = new LineSink();
    const parcelRanges: TriRange[] = [];
    for (const p of chunk.parcels) {
      const start = plots.triangleCount;
      let grass = P.shade(P.GRASS_BASE, 0.92 + 0.16 * P.hash01(p.id));
      if (p.listing) grass = P.shade(grass, 1.08);
      fillPolygon(plots, p.polygon, proj, Y.plot, grass);
      parcelRanges.push({ start, end: plots.triangleCount, id: p.id });

      const mine = p.owner !== null && p.owner.id === ctx.myId;
      const selected = p.id === ctx.selectedId;
      let accent: number | null = null;
      let line: number | null = null;
      if (ctx.mapLayer === "foot_traffic") {
        // Green = packed with customers, red = quiet — matches the legend.
        accent = P.mixRgb(P.TRAFFIC_GROUND_QUIET, P.TRAFFIC_GROUND_BUSY, Math.min(1, p.busyness / 3));
      } else if (ctx.mapLayer === "logistics") {
        if (mine) {
          accent = P.ACCENT_MINE;
          line = P.ACCENT_MINE;
        }
      } else if (mine) {
        accent = P.ACCENT_MINE;
        line = P.ACCENT_MINE;
      } else if (p.owner) {
        line = P.ACCENT_THEIRS;
      } else if (p.listing) {
        line = P.ACCENT_LISTING;
      }

      if (accent !== null) fillPolygon(accents, p.polygon, proj, Y.accent, accent);
      if (selected) {
        outlines.ring(p.polygon, proj, Y.outline, P.ACCENT_SELECTED);
        outlines.ring(p.polygon, proj, Y.outline + 1.8, P.ACCENT_SELECTED);
      } else {
        // Every lot carries the reference's thin light-green cadastre line;
        // ownership and listings just recolour it.
        outlines.ring(p.polygon, proj, Y.outline, line ?? P.PLOT_LINE);
      }
    }
    const parcelMesh = addMesh(group, plots);
    addMesh(group, accents, { transparent: true, opacity: 0.42 });
    addLines(group, outlines);

    // Buildings: sun-baked walls dressed by the shared window grid, roofs by
    // building character (pitched houses, flat slabs, dark industrial), and a
    // translucent projected shadow; the cutaway building is simply omitted.
    const walls = new MeshSink();
    const roofs = new MeshSink();
    const shadows = new MeshSink();
    const greens = new MeshSink();
    const buildingRanges: TriRange[] = [];
    const roofRanges: TriRange[] = [];
    const builtParcels = new Set<string>();
    for (const b of chunk.buildings) {
      builtParcels.add(b.parcelId);
      if (b.id === ctx.cutawayBuildingId) {
        // The cutaway keeps its lower storeys: exterior walls rise to the
        // selected floor's slab, the roof and everything above come off. The
        // ground floor cuts away completely — the plan sits on the ground.
        const cutPts = dedupedRing(b.footprint, proj);
        if (cutPts.length >= 3 && ctx.cutawayBaseM > 0.3) {
          const wallStart = walls.triangleCount;
          extrudeWalls(walls, b.footprint, proj, ctx.cutawayBaseM, P.hashPick(P.FACADES, b.id));
          buildingRanges.push({ start: wallStart, end: walls.triangleCount, id: b.parcelId });
          projectedShadow(shadows, cutPts, ctx.cutawayBaseM, Y.shadow, P.SHADOW);
        }
        continue;
      }
      const pts = dedupedRing(b.footprint, proj);
      if (pts.length < 3) continue;
      const area = Math.abs(ringArea(pts));
      const industrial = area > 1_600 && b.floors <= 3;
      const house = !industrial && b.floors <= 2 && area < 420;

      let wall = industrial
        ? P.WALL_INDUSTRIAL
        : b.floors > 5
          ? P.hashPick(P.FACADE_TOWERS, b.id)
          : P.hashPick(P.FACADES, b.id);
      const mine = b.owner !== null && b.owner.id === ctx.myId;
      if (mine) wall = P.mixRgb(wall, P.ACCENT_MINE, 0.2);
      if (ctx.selectedId !== null && b.parcelId === ctx.selectedId) {
        wall = P.shade(wall, 1.15);
      }

      const wallStart = walls.triangleCount;
      const roofStart = roofs.triangleCount;
      extrudeWalls(walls, b.footprint, proj, b.heightM, wall);
      if (house) {
        const roofColour = P.hashPick(P.ROOF_PITCHED, b.id, "roof");
        if (!gableRoof(walls, roofs, pts, b.heightM, roofColour, wall)) {
          flatRoof(roofs, pts, b.heightM, roofColour);
        }
      } else {
        const flat = industrial ? P.ROOF_INDUSTRIAL : P.ROOF_FLAT;
        flatRoof(roofs, pts, b.heightM, P.shade(flat, 0.92 + 0.16 * P.hash01(b.id + "r")));
      }
      buildingRanges.push({ start: wallStart, end: walls.triangleCount, id: b.parcelId });
      roofRanges.push({ start: roofStart, end: roofs.triangleCount, id: b.parcelId });
      projectedShadow(shadows, pts, b.heightM, Y.shadow, P.SHADOW);
    }

    // Trees on open, unbuilt, unlisted land — the reference city's parks.
    let treeBudget = 120;
    for (const p of chunk.parcels) {
      if (treeBudget <= 0) break;
      if (builtParcels.has(p.id) || p.owner || p.listing) continue;
      const pts = dedupedRing(p.polygon, proj);
      if (pts.length < 3) continue;
      const area = Math.abs(ringArea(pts));
      if (area < 600) continue;
      let cx = 0;
      let cz = 0;
      for (const [x, z] of pts) {
        cx += x / pts.length;
        cz += z / pts.length;
      }
      const count = Math.min(treeBudget, 1 + Math.floor(P.hash01(p.id + "t") * 3));
      for (let i = 0; i < count; i++) {
        // Slide from the centroid partway to a hash-picked vertex: stays
        // inside anything convex-ish without a point-in-polygon test.
        const v = pts[Math.floor(P.hash01(p.id + "v" + i) * pts.length) % pts.length]!;
        const t = 0.3 + 0.35 * P.hash01(p.id + "d" + i);
        tree(
          greens,
          cx + (v[0] - cx) * t,
          cz + (v[1] - cz) * t,
          0.8 + 0.6 * P.hash01(p.id + "s" + i),
          P.shade(P.TREE_CANOPY, 0.85 + 0.3 * P.hash01(p.id + "c" + i)),
          P.TREE_TRUNK,
        );
        treeBudget--;
      }
    }

    addMesh(group, shadows, { transparent: true, opacity: P.SHADOW_OPACITY });
    const buildingMesh = addMesh(group, walls, { map: this.windowTexture });
    const roofMesh = addMesh(group, roofs);
    addMesh(group, greens);

    // Cars amble the wider roads, offset into the right-hand lane.
    const cars: Car[] = [];
    for (const r of chunk.roads ?? []) {
      if (cars.length >= 14) break;
      if (r.halfWidthM < 2.6 || r.line.length < 2) continue;
      for (const reverse of [false, true]) {
        const path = laneOffsetPath(r.line, proj, r.halfWidthM * 0.45, reverse);
        if (!path) continue;
        const { pts, cum, total } = path;
        if (total < 80) continue;
        const n = Math.min(3, Math.max(1, Math.floor(total / 260)));
        for (let i = 0; i < n && cars.length < 14; i++) {
          const mesh = new Mesh(
            carGeometry,
            carMaterials[Math.floor(P.hash01(r.id + reverse + i) * carMaterials.length)]!,
          );
          mesh.userData.shared = true;
          group.add(mesh);
          cars.push({
            mesh,
            pts,
            cum,
            total,
            speed: 7 + 7 * P.hash01(r.id + "s" + reverse + i),
            phase: P.hash01(r.id + "p" + reverse + i),
            reverse,
          });
        }
      }
    }

    // Pedestrians stream the sidewalks, denser where the parcels are busy —
    // the reference's streets read as alive at every zoom.
    let busySum = 0;
    for (const p of chunk.parcels) busySum += p.busyness;
    const busyMult = clamp(0.6 + (busySum / Math.max(1, chunk.parcels.length)) * 0.8, 0.6, 2);
    const walkers: Walker[] = [];
    for (const r of chunk.roads ?? []) {
      if (walkers.length >= 170) break;
      if (r.halfWidthM < 2.2 || r.line.length < 2) continue;
      for (const reverse of [false, true]) {
        const path = laneOffsetPath(r.line, proj, r.halfWidthM + 1.1, reverse);
        if (!path || path.total < 40) continue;
        const n = Math.min(12, Math.floor((path.total / 42) * busyMult));
        for (let i = 0; i < n && walkers.length < 170; i++) {
          walkers.push({
            pts: path.pts,
            cum: path.cum,
            total: path.total,
            speed: 0.9 + 0.8 * P.hash01(r.id + "pv" + reverse + i),
            phase: P.hash01(r.id + "pp" + reverse + i),
            scale: 0.85 + 0.3 * P.hash01(r.id + "ps" + reverse + i),
          });
        }
      }
    }
    let peds: PedCrowd | null = null;
    if (walkers.length > 0) {
      const bodies = new InstancedMesh(pedBodyGeometry, pedBodyMaterial, walkers.length);
      const heads = new InstancedMesh(pedHeadGeometry, pedHeadMaterial, walkers.length);
      bodies.userData.shared = true;
      heads.userData.shared = true;
      const tint = new Color();
      for (let i = 0; i < walkers.length; i++) {
        bodies.setColorAt(i, tint.setHex(P.PED_BODIES[i % P.PED_BODIES.length]!));
      }
      group.add(bodies, heads);
      peds = { bodies, heads, walkers };
    }

    return {
      group,
      chunk,
      ctxKey,
      parcelRanges,
      parcelMesh,
      buildingRanges,
      buildingMesh,
      roofRanges,
      roofMesh,
      cars,
      peds,
    };
  }

  setFloorPlan(plan: FloorPlanResponse | null, selectedRoomId: string | null, myId: string | null): void {
    disposeGroup(this.roomGroup);
    this.roomRanges = [];
    this.roomMesh = null;
    if (!plan) return;

    // The plan sits on the selected floor's slab; partition walls rise most
    // of a storey so the camera reads real rooms, not painted rectangles.
    const storey = plan.heightM / Math.max(1, plan.floors);
    const base = storey * plan.floor;
    const wallH = Math.min(2.8, Math.max(1.8, storey * 0.85));

    const slabs = new MeshSink();
    const partitions = new MeshSink();
    const lines = new LineSink();
    const dashes = new LineSink();

    for (const unit of plan.units) {
      for (const room of unit.rooms) {
        const mine = room.owner !== null && room.owner.id === myId;
        const colour = mine
          ? P.ROOM_MINE
          : room.owner
            ? P.ROOM_TAKEN
            : !room.sellable
              ? P.ROOM_COMMON
              : room.id === selectedRoomId
                ? P.ROOM_SELECTED
                : P.ROOM_SELLABLE;

        // Floor fill, pickable.
        const start = slabs.triangleCount;
        fillPolygon(slabs, room.polygon, this.proj, base + 0.3, colour);
        extrudeSlab(slabs, room.polygon, this.proj, base, base + 0.3, P.shade(colour, 0.6));
        this.roomRanges.push({ start, end: slabs.triangleCount, id: room.id });

        // Each room draws its own walls a hand inside its boundary: shared
        // walls become two skins with an air gap instead of one z-fighting
        // plane, and together they read as wall thickness.
        const pts = dedupedRing(room.polygon, this.proj);
        if (pts.length >= 3) {
          const inner = insetRing(pts, 0.09);
          partitionWalls(partitions, inner, base + 0.3, base + wallH, room.id === selectedRoomId);
          // The reference's dashed inner outline.
          dashedRing(dashes, insetRing(pts, 0.55), base + 0.42, P.ROOM_DASH);
        }
        if (room.id === selectedRoomId) {
          lines.ring(room.polygon, this.proj, base + wallH + 0.15, 0xffffff);
        }
      }
    }
    this.roomMesh = addMesh(this.roomGroup, slabs);
    addMesh(this.roomGroup, partitions);
    addLines(this.roomGroup, lines);
    addLines(this.roomGroup, dashes);
  }

  setLanes(lanes: Array<{ from: Vec2; to: Vec2 }>): void {
    disposeGroup(this.laneGroup);
    this.couriers = [];
    if (lanes.length === 0) return;

    const lines = new LineSink();
    const dot = new SphereGeometry(7, 10, 8);
    const dotMaterial = new MeshBasicMaterial({ color: P.LANE_COURIER });
    for (const lane of lanes) {
      const [ax, az] = this.proj.toWorld(lane.from);
      const [bx, bz] = this.proj.toWorld(lane.to);
      lines.segment(ax, 8, az, bx, 8, bz, P.LANE_LINE);
      const mesh = new Mesh(dot, dotMaterial);
      this.laneGroup.add(mesh);
      this.couriers.push({
        mesh,
        from: new Vector3(ax, 8, az),
        to: new Vector3(bx, 8, bz),
        // Slower on long hauls, never glacial: reads as traffic, not physics.
        period: clamp(Math.hypot(bx - ax, bz - az) / 120, 4, 18),
        phase: Math.random(),
      });
    }
    addLines(this.laneGroup, lines);
  }

  /** Whether the camera is far enough out to be in world-map view. */
  isMapView(): boolean {
    return this.mapView;
  }

  /** Build the atlas layer once, the first time the camera leaves the city. */
  private ensureWorldMap(): void {
    if (this.mapBuildStarted) return;
    this.mapBuildStarted = true;
    void import("./worldmap")
      .then(({ buildWorldMap }) => buildWorldMap(this.proj, addMesh, addLines))
      .then((built) => {
        if (this.disposed) return;
        this.atlasGroup = built.group;
        this.mapGroup.add(built.group);
        this.mapPlaces = built.places.map((p) => {
          const [x, z] = this.proj.toWorld(p.lngLat);
          const el = document.createElement("span");
          el.className = `map-place ${p.kind}`;
          el.textContent = p.name;
          el.style.display = "none";
          this.opts.labelLayer.appendChild(el);
          return { pos: new Vector3(x, 0, z), el, kind: p.kind };
        });
      })
      .catch((err: unknown) => console.error("world map failed to build:", err));
  }

  /**
   * Replace the detailed political layer with a fresh viewport's worth of
   * admin areas. Cheap enough to rebuild per response — the server caps the
   * payload — and replacing wholesale keeps panning simple.
   */
  setAdminAreas(resp: AdminAreasResponse): void {
    disposeGroup(this.detailMapGroup);
    const built = buildAdminAreas(resp, this.proj);
    addMesh(this.detailMapGroup, built.fills);
    addLines(this.detailMapGroup, built.borders);
    this.detailLoaded = resp.areas.length > 0;

    for (const p of this.adminPlaces) p.el.remove();
    this.adminPlaces = built.places.map((p) => {
      const [x, z] = this.proj.toWorld(p.lngLat);
      const el = document.createElement("span");
      el.className = "map-place admin1";
      el.textContent = p.name;
      el.style.display = "none";
      this.opts.labelLayer.appendChild(el);
      return { pos: new Vector3(x, 0, z), el, sizeM: p.sizeM };
    });
  }

  /** City/town labels for the map view, replaced per viewport fetch. */
  setMapLabels(labels: MapLabelFeature[]): void {
    for (const p of this.cityPlaces) p.el.remove();
    this.cityPlaces = labels.map((l) => {
      const [x, z] = this.proj.toWorld(l.point);
      const el = document.createElement("span");
      el.className = `map-place city ${l.kind}`;
      el.textContent = l.name;
      el.style.display = "none";
      this.opts.labelLayer.appendChild(el);
      return { pos: new Vector3(x, 0, z), el, rank: l.rank };
    });
  }

  /**
   * Uncapped viewport for map-view fetches (admin areas, labels). Unlike
   * viewBounds() this may span whole continents; when the horizon rays miss
   * the ground it falls back to a span proportional to camera distance.
   */
  mapViewBounds(): { west: number; south: number; east: number; north: number } {
    const centre = this.cameraLngLat();
    const fallbackDeg = Math.min(90, (this.dist * 3) / 111_320);
    let west = centre[0];
    let east = centre[0];
    let south = centre[1];
    let north = centre[1];
    let hits = 0;
    for (const [sx, sy] of [
      [-1, -1],
      [1, -1],
      [-1, 0.3],
      [1, 0.3],
      [0, 1],
    ] as const) {
      const hit = this.groundAtScreen(sx, sy);
      if (!hit) continue;
      hits += 1;
      const [lon, lat] = this.proj.toLngLat(hit.x, hit.z);
      west = Math.min(west, lon);
      east = Math.max(east, lon);
      south = Math.min(south, lat);
      north = Math.max(north, lat);
    }
    if (hits < 5) {
      west = Math.min(west, centre[0] - fallbackDeg / 2);
      east = Math.max(east, centre[0] + fallbackDeg / 2);
      south = Math.min(south, centre[1] - fallbackDeg / 2);
      north = Math.max(north, centre[1] + fallbackDeg / 2);
    }
    return {
      west: Math.max(-180, west),
      south: Math.max(-85, south),
      east: Math.min(180, east),
      north: Math.min(85, north),
    };
  }

  /** Major roads of every generated chunk, drawn faintly on the map view. */
  setOverviewRoads(lines: Array<Array<Vec2>>): void {
    disposeGroup(this.overviewGroup);
    if (lines.length === 0) return;
    const sink = new LineSink();
    for (const line of lines) {
      const pts = line.map((p) => this.proj.toWorld(p));
      for (let i = 0; i < pts.length - 1; i++) {
        sink.segment(pts[i]![0], 60, pts[i]![1], pts[i + 1]![0], 60, pts[i + 1]![1], P.OVERVIEW_ROAD);
      }
    }
    addLines(this.overviewGroup, sink);
  }

  /** Clustered player-business markers, shown only in map view. */
  setMapMarkers(specs: LabelSpec[]): void {
    for (const m of this.mapMarkers) m.el.remove();
    this.mapMarkers = specs.map((spec) => {
      const [x, z] = this.proj.toWorld(spec.lngLat);
      spec.el.style.display = "none";
      this.opts.labelLayer.appendChild(spec.el);
      return { pos: new Vector3(x, 0, z), el: spec.el };
    });
  }

  setLabels(specs: LabelSpec[]): void {
    for (const label of this.labels) label.el.remove();
    this.labels = specs.map((spec) => {
      const [x, z] = this.proj.toWorld(spec.lngLat);
      this.opts.labelLayer.appendChild(spec.el);
      return { pos: new Vector3(x, spec.altitude ?? 0, z), el: spec.el };
    });
  }

  /* ---------------- internals ---------------- */

  private resize(): void {
    const w = this.opts.container.clientWidth || 1;
    const h = this.opts.container.clientHeight || 1;
    this.renderer.setSize(w, h);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
  }

  private frame(now: number): void {
    const dt = Math.min(0.1, (now - this.lastTime) / 1000);
    this.lastTime = now;

    // Critically-damped ease towards the desired camera. Snaps when close so
    // idle detection has a stable "settled" state.
    const k = 1 - Math.exp(-dt * 7);
    this.target.lerp(this.desiredTarget, k);
    this.dist += (this.desiredDist - this.dist) * k;
    this.azimuth += (this.desiredAzimuth - this.azimuth) * k;
    this.elevation += (this.desiredElevation - this.elevation) * k;
    const settled =
      this.target.distanceTo(this.desiredTarget) < 0.5 &&
      Math.abs(this.dist - this.desiredDist) < 1 &&
      Math.abs(this.azimuth - this.desiredAzimuth) < 0.001 &&
      Math.abs(this.elevation - this.desiredElevation) < 0.001;
    if (settled && this.moved) {
      this.moved = false;
      if (this.idleTimer !== null) window.clearTimeout(this.idleTimer);
      this.idleTimer = window.setTimeout(() => this.opts.onIdle(), IDLE_MS);
    }

    // Load the atlas slightly before the handover so it never pops in late.
    if (this.dist > MAP_PRELOAD_DIST) this.ensureWorldMap();

    // Hand over between the 3D city and the world map at the mode boundary.
    const wantMap = this.dist > MAP_VIEW_DIST;
    if (wantMap !== this.mapView) {
      this.mapView = wantMap;
      this.worldGroup.visible = !wantMap;
      this.laneGroup.visible = !wantMap;
      this.roomGroup.visible = !wantMap;
      this.mapGroup.visible = wantMap;
      this.overviewGroup.visible = wantMap;
      this.ground.material = wantMap ? this.oceanMaterial : this.grassMaterial;
      if (!wantMap) {
        for (const p of this.mapPlaces) p.el.style.display = "none";
        for (const p of this.adminPlaces) p.el.style.display = "none";
        for (const p of this.cityPlaces) p.el.style.display = "none";
        for (const m of this.mapMarkers) m.el.style.display = "none";
      }
    }

    // The detailed admin layer carries the map below continental range; the
    // coarse bundled atlas covers everything beyond it (and fills in until
    // the first admin-area response arrives).
    if (this.mapView) {
      const wantDetail = this.detailLoaded && this.dist < DETAIL_MAP_DIST;
      this.detailMapGroup.visible = wantDetail;
      if (this.atlasGroup) this.atlasGroup.visible = !wantDetail;
    }

    // The resources heatmap follows the camera in both views, floating above
    // the city accents or above the political map layers.
    if (this.resourceLayer) {
      this.resourceLayer.mesh.visible = this.showResources;
      if (this.showResources) {
        this.resourceLayer.update(
          this.proj,
          this.target.x,
          this.target.z,
          this.dist,
          this.mapView ? 120 : 1.2,
        );
      }
    }

    // Haze fades out as the camera pulls back, so the screen never washes
    // solid before the map takes over; gone entirely in map view.
    this.fog.density = this.mapView
      ? 0
      : FOG_DENSITY * smootherstep((FOG_CLEAR_DIST - this.dist) / (FOG_CLEAR_DIST - FOG_FULL_DIST));

    // Past planet-framing distances the sky gives way to space.
    (this.scene.background as Color)
      .copy(this.skyColor)
      .lerp(this.spaceColor, smootherstep((this.dist - SPACE_NEAR_DIST) / (SPACE_FAR_DIST - SPACE_NEAR_DIST)));

    // Follow in whole grass tiles so the ground texture never slides; in map
    // view the plane also scales with the camera so the ocean has no edge.
    this.ground.position.set(
      Math.round(this.target.x / GRASS_TILE) * GRASS_TILE,
      -0.5,
      Math.round(this.target.z / GRASS_TILE) * GRASS_TILE,
    );
    const groundScale = Math.max(1, (this.dist * 8) / GROUND_SPAN);
    this.ground.scale.setScalar(groundScale);

    // Planet-scale zoom needs the clip planes to travel with the camera.
    this.camera.near = Math.max(4, this.dist / 4_000);
    this.camera.far = Math.max(60_000, this.dist * 6);
    this.camera.updateProjectionMatrix();

    const cosE = Math.cos(this.elevation);
    this.camera.position.set(
      this.target.x + Math.sin(this.azimuth) * cosE * this.dist,
      Math.sin(this.elevation) * this.dist,
      this.target.z + Math.cos(this.azimuth) * cosE * this.dist,
    );
    this.camera.lookAt(this.target);

    // The avatar walks a straight line at a fixed pace; the camera glides
    // along while following. Hidden when the camera is too far to matter.
    this.playerGroup.visible = !this.mapView && this.dist < PLAYER_VISIBLE_DIST;
    if (this.walkTarget) {
      const to = new Vector3().subVectors(this.walkTarget, this.playerPos);
      to.y = 0;
      const remaining = to.length();
      const step = WALK_SPEED_MPS * dt;
      if (remaining <= Math.max(ARRIVE_EPS_M, step)) {
        this.playerPos.copy(this.walkTarget);
        this.walkTarget = null;
      } else {
        to.normalize();
        this.playerPos.addScaledVector(to, step);
        this.playerGroup.rotation.y = Math.atan2(to.x, to.z);
        // Path line from the avatar's feet to the destination ring.
        const pos = this.pathLine.geometry.getAttribute("position") as BufferAttribute;
        pos.setXYZ(0, this.playerPos.x, 0.9, this.playerPos.z);
        pos.setXYZ(1, this.walkTarget.x, 0.9, this.walkTarget.z);
        pos.needsUpdate = true;
        const pulse = 1 + 0.15 * Math.sin(now / 180);
        this.targetRing.scale.setScalar(pulse);
      }
      this.playerGroup.position.copy(this.playerPos);
      if (this.followPlayer) {
        this.desiredTarget.copy(this.playerPos);
        this.moved = true;
      }
    }
    const walking = this.walkTarget !== null;
    this.pathLine.visible = walking && this.playerGroup.visible;
    this.targetRing.visible = walking && !this.mapView;

    if (!this.mapView) {
      // Couriers pulse along their lanes while the logistics overlay is up.
      for (const c of this.couriers) {
        const t = ((now / 1000 + c.phase * c.period) % c.period) / c.period;
        c.mesh.position.lerpVectors(c.from, c.to, t);
      }

      // Cars roll their lanes, wrapping at the chunk edge.
      for (const entry of this.chunkMeshes.values()) {
        for (const car of entry.cars) {
          const s = (now / 1000) * car.speed + car.phase * car.total;
          placeCar(car, s % car.total);
        }
        if (entry.peds) placeWalkers(entry.peds, now);
      }
    }

    this.positionLabels();
    this.positionMapLabels();
    this.renderer.render(this.scene, this.camera);

    if (this.opts.fpsNode) {
      this.fpsAccum += dt;
      this.fpsFrames += 1;
      if (this.fpsAccum >= 0.5) {
        this.opts.fpsNode.textContent = `${Math.round(this.fpsFrames / this.fpsAccum)} fps`;
        this.fpsAccum = 0;
        this.fpsFrames = 0;
      }
    }
  }

  private positionLabels(): void {
    if (this.labels.length === 0) return;
    const w = this.opts.container.clientWidth;
    const h = this.opts.container.clientHeight;
    const tooFar = this.dist > LABEL_DIST;
    const v = new Vector3();
    for (const label of this.labels) {
      v.copy(label.pos).project(this.camera);
      const visible = !tooFar && v.z < 1 && v.x > -1.05 && v.x < 1.05 && v.y > -1.05 && v.y < 1.05;
      label.el.style.display = visible ? "" : "none";
      if (visible) {
        const x = ((v.x + 1) / 2) * w;
        const y = ((1 - v.y) / 2) * h;
        label.el.style.transform = `translate(-50%, -50%) translate(${x.toFixed(1)}px, ${y.toFixed(1)}px)`;
      }
    }
  }

  /** Country/continent names and business markers, map view only. */
  private positionMapLabels(): void {
    if (!this.mapView) return;
    const w = this.opts.container.clientWidth;
    const h = this.opts.container.clientHeight;
    const v = new Vector3();
    const continentsOnly = this.dist > CONTINENT_DIST;

    const place = (pos: Vector3, el: HTMLElement, show: boolean): void => {
      if (!show) {
        el.style.display = "none";
        return;
      }
      v.copy(pos).project(this.camera);
      const visible = v.z < 1 && v.x > -1.05 && v.x < 1.05 && v.y > -1.05 && v.y < 1.05;
      el.style.display = visible ? "" : "none";
      if (visible) {
        const x = ((v.x + 1) / 2) * w;
        const y = ((1 - v.y) / 2) * h;
        el.style.transform = `translate(-50%, -50%) translate(${x.toFixed(1)}px, ${y.toFixed(1)}px)`;
      }
    };

    // Label ladder: continents → countries → regions and cities, by camera
    // distance. Countries stay up through the region band so a UK view reads
    // "United Kingdom" over its counties, like the reference.
    const showCountries = !continentsOnly && this.dist > COUNTRY_LABEL_MIN_DIST;
    const showRegions = this.dist <= DETAIL_MAP_DIST;
    const cityMaxRank = this.dist < 100_000 ? 10 : this.dist < 400_000 ? 7 : 4;
    // A region's name only appears once the region itself reads at this
    // distance — big counties from afar, small boroughs up close.
    const minRegionSizeM = this.dist * 0.06;
    for (const p of this.mapPlaces) {
      place(p.pos, p.el, continentsOnly ? p.kind === "continent" : p.kind === "country" && showCountries);
    }
    for (const p of this.adminPlaces) place(p.pos, p.el, showRegions && p.sizeM > minRegionSizeM);
    for (const p of this.cityPlaces) place(p.pos, p.el, showRegions && p.rank <= cityMaxRank);
    for (const m of this.mapMarkers) place(m.pos, m.el, true);
  }

  /** Ray → ground plane (y = 0) for a normalised screen point. */
  private groundAtScreen(sx: number, sy: number): Vector3 | null {
    this.raycaster.setFromCamera(new Vector2(sx, sy), this.camera);
    const dir = this.raycaster.ray.direction;
    const origin = this.raycaster.ray.origin;
    if (dir.y >= -1e-4) return null;
    const t = -origin.y / dir.y;
    return new Vector3(origin.x + dir.x * t, 0, origin.z + dir.z * t);
  }

  private attachInput(): () => void {
    const el = this.renderer.domElement;
    let mode: "none" | "pan" | "orbit" = "none";
    let grab: Vector3 | null = null;
    let downAt = 0;
    let downX = 0;
    let downY = 0;
    let movedPx = 0;
    let lastX = 0;
    let lastY = 0;

    const toNdc = (e: PointerEvent | WheelEvent): [number, number] => {
      const r = el.getBoundingClientRect();
      return [((e.clientX - r.left) / r.width) * 2 - 1, -(((e.clientY - r.top) / r.height) * 2 - 1)];
    };

    const onPointerDown = (e: PointerEvent): void => {
      if (e.button !== 0 && e.button !== 2) return;
      try {
        el.setPointerCapture(e.pointerId);
      } catch {
        // Synthetic pointers (tests, automation) cannot be captured. The drag
        // still works while the cursor stays over the canvas.
      }
      const orbit = e.button === 2 || e.ctrlKey || e.metaKey;
      mode = orbit ? "orbit" : "pan";
      const [sx, sy] = toNdc(e);
      grab = this.groundAtScreen(sx, sy);
      downAt = performance.now();
      downX = e.clientX;
      downY = e.clientY;
      movedPx = 0;
      lastX = e.clientX;
      lastY = e.clientY;
    };

    const onPointerMove = (e: PointerEvent): void => {
      if (mode === "none") return;
      movedPx = Math.max(movedPx, Math.hypot(e.clientX - downX, e.clientY - downY));
      const dx = e.clientX - lastX;
      const dy = e.clientY - lastY;
      lastX = e.clientX;
      lastY = e.clientY;
      if (mode === "orbit") {
        this.desiredAzimuth -= dx * 0.005;
        this.desiredElevation = clamp(this.desiredElevation + dy * 0.004, 0.42, TILT_TOP);
        this.moved = true;
        return;
      }
      // Pan: keep the grabbed ground point under the cursor. Applied without
      // easing — smoothing a pan reads as lag, not polish. Grabbing the world
      // also takes the camera back from the walking avatar.
      if (movedPx >= 5) this.followPlayer = false;
      if (!grab) return;
      const [sx, sy] = toNdc(e);
      const nowAt = this.groundAtScreen(sx, sy);
      if (!nowAt) return;
      const shift = new Vector3().subVectors(grab, nowAt);
      shift.y = 0;
      // Clamp: near the horizon a tiny cursor move maps to kilometres.
      if (shift.length() > this.dist * 2) shift.setLength(this.dist * 2);
      this.target.add(shift);
      this.desiredTarget.copy(this.target);
      this.moved = true;
    };

    const onPointerUp = (e: PointerEvent): void => {
      if (mode === "none") return;
      const wasPan = mode === "pan";
      const wasRightClick = mode === "orbit" && e.button === 2;
      mode = "none";
      grab = null;
      if (wasPan && movedPx < 5 && performance.now() - downAt < 500) {
        const [sx, sy] = toNdc(e);
        this.pick(sx, sy);
        return;
      }
      // A right-click that never became a drag sends the avatar walking.
      if (wasRightClick && movedPx < 5 && performance.now() - downAt < 400 && !this.mapView) {
        const [sx, sy] = toNdc(e);
        const at = this.groundAtScreen(sx, sy);
        if (at) this.walkTo(at);
      }
    };

    const onWheel = (e: WheelEvent): void => {
      e.preventDefault();
      const factor = Math.exp(-e.deltaY * 0.0014);
      const next = clamp(this.desiredDist / factor, MIN_DIST, MAX_DIST);
      const applied = this.desiredDist / next;
      // Zoom towards the cursor: the point under it stays put.
      const [sx, sy] = toNdc(e);
      const at = this.groundAtScreen(sx, sy);
      if (at && applied > 1) {
        this.desiredTarget.lerp(at, 1 - 1 / applied);
      }
      this.desiredDist = next;
      this.moved = true;
    };

    const onDblClick = (e: MouseEvent): void => {
      const r = el.getBoundingClientRect();
      const sx = ((e.clientX - r.left) / r.width) * 2 - 1;
      const sy = -(((e.clientY - r.top) / r.height) * 2 - 1);
      const at = this.groundAtScreen(sx, sy);
      if (at) {
        this.desiredTarget.copy(at);
        this.desiredDist = clamp(this.desiredDist * 0.55, MIN_DIST, MAX_DIST);
        this.moved = true;
      }
    };

    const onContextMenu = (e: Event): void => e.preventDefault();

    el.addEventListener("pointerdown", onPointerDown);
    el.addEventListener("pointermove", onPointerMove);
    el.addEventListener("pointerup", onPointerUp);
    el.addEventListener("pointercancel", onPointerUp);
    el.addEventListener("wheel", onWheel, { passive: false });
    el.addEventListener("dblclick", onDblClick);
    el.addEventListener("contextmenu", onContextMenu);
    return () => {
      el.removeEventListener("pointerdown", onPointerDown);
      el.removeEventListener("pointermove", onPointerMove);
      el.removeEventListener("pointerup", onPointerUp);
      el.removeEventListener("pointercancel", onPointerUp);
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("dblclick", onDblClick);
      el.removeEventListener("contextmenu", onContextMenu);
    };
  }

  /** Rooms first when a plan is open, then buildings, then ground parcels. */
  private pick(sx: number, sy: number): void {
    this.raycaster.setFromCamera(new Vector2(sx, sy), this.camera);

    if (this.roomMesh) {
      const hit = this.raycaster.intersectObject(this.roomMesh, false)[0];
      const id = hit && rangeAt(this.roomRanges, hit.faceIndex ?? -1);
      if (id) {
        this.opts.onPickRoom(id);
        return;
      }
    }

    let best: { distance: number; id: string } | null = null;
    for (const entry of this.chunkMeshes.values()) {
      for (const [mesh, ranges] of [
        [entry.buildingMesh, entry.buildingRanges],
        [entry.roofMesh, entry.roofRanges],
      ] as const) {
        if (!mesh) continue;
        const hit = this.raycaster.intersectObject(mesh, false)[0];
        const id = hit && rangeAt(ranges, hit.faceIndex ?? -1);
        if (hit && id && (!best || hit.distance < best.distance)) {
          best = { distance: hit.distance, id };
        }
      }
    }
    if (!best) {
      for (const entry of this.chunkMeshes.values()) {
        if (entry.parcelMesh) {
          const hit = this.raycaster.intersectObject(entry.parcelMesh, false)[0];
          const id = hit && rangeAt(entry.parcelRanges, hit.faceIndex ?? -1);
          if (hit && id && (!best || hit.distance < best.distance)) {
            best = { distance: hit.distance, id };
          }
        }
      }
    }
    if (best) this.opts.onPickParcel(best.id);
  }
}

/* ---------------- helpers ---------------- */

/** Clamped Hermite ease: 0 at t≤0, 1 at t≥1, smooth in between. */
function smootherstep(t: number): number {
  const c = clamp(t, 0, 1);
  return c * c * (3 - 2 * c);
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

function rangeAt(ranges: TriRange[], faceIndex: number): string | null {
  for (const r of ranges) {
    if (faceIndex >= r.start && faceIndex < r.end) return r.id;
  }
  return null;
}

/** Signed area of a projected ring (xz plane). */
function ringArea(pts: ReadonlyArray<[number, number]>): number {
  let area = 0;
  for (let i = 0; i < pts.length; i++) {
    const [ax, az] = pts[i]!;
    const [bx, bz] = pts[(i + 1) % pts.length]!;
    area += (ax * bz - bx * az) / 2;
  }
  return area;
}

/**
 * A road centre-line pushed into its right-hand lane (per travel direction),
 * with cumulative distances for constant-speed traversal.
 */
function laneOffsetPath(
  line: readonly Vec2[],
  proj: Projection,
  offset: number,
  reverse: boolean,
): { pts: Array<[number, number]>; cum: number[]; total: number } | null {
  const src = line.map((p) => proj.toWorld(p));
  if (reverse) src.reverse();
  const pts: Array<[number, number]> = [];
  for (let i = 0; i < src.length; i++) {
    const [ax, az] = src[Math.max(0, i - 1)]!;
    const [bx, bz] = src[Math.min(src.length - 1, i + 1)]!;
    const dx = bx - ax;
    const dz = bz - az;
    const len = Math.hypot(dx, dz);
    if (len < 0.01) continue;
    // Right-hand side of travel: normal (dz, -dx) flipped for y-up xz.
    pts.push([src[i]![0] + (-dz / len) * -offset, src[i]![1] + (dx / len) * -offset]);
  }
  if (pts.length < 2) return null;
  const cum = [0];
  for (let i = 1; i < pts.length; i++) {
    cum.push(cum[i - 1]! + Math.hypot(pts[i]![0] - pts[i - 1]![0], pts[i]![1] - pts[i - 1]![1]));
  }
  const total = cum[cum.length - 1]!;
  return total > 1 ? { pts, cum, total } : null;
}

/** Position along a cumulative-length path at distance s. */
function pathAt(pts: Array<[number, number]>, cum: number[], s: number): [number, number] {
  let i = 1;
  while (i < cum.length - 1 && cum[i]! < s) i++;
  const segLen = cum[i]! - cum[i - 1]! || 1;
  const t = (s - cum[i - 1]!) / segLen;
  const [ax, az] = pts[i - 1]!;
  const [bx, bz] = pts[i]!;
  return [ax + (bx - ax) * t, az + (bz - az) * t];
}

const walkerMatrix = new Matrix4();
const walkerPos = new Vector3();
const walkerScale = new Vector3();
const walkerQuat = new Quaternion();

/** Advance every walker in a crowd and push the new instance transforms. */
function placeWalkers(crowd: PedCrowd, now: number): void {
  for (let i = 0; i < crowd.walkers.length; i++) {
    const w = crowd.walkers[i]!;
    const s = ((now / 1000) * w.speed + w.phase * w.total) % w.total;
    const [x, z] = pathAt(w.pts, w.cum, s);
    walkerScale.setScalar(w.scale);
    walkerPos.set(x, 0.62 * w.scale, z);
    walkerMatrix.compose(walkerPos, walkerQuat, walkerScale);
    crowd.bodies.setMatrixAt(i, walkerMatrix);
    walkerPos.y = 1.5 * w.scale;
    walkerMatrix.compose(walkerPos, walkerQuat, walkerScale);
    crowd.heads.setMatrixAt(i, walkerMatrix);
  }
  crowd.bodies.instanceMatrix.needsUpdate = true;
  crowd.heads.instanceMatrix.needsUpdate = true;
}

/** Put a car at distance s along its path, heading along the segment. */
function placeCar(car: Car, s: number): void {
  const { pts, cum } = car;
  let i = 1;
  while (i < cum.length - 1 && cum[i]! < s) i++;
  const segLen = cum[i]! - cum[i - 1]! || 1;
  const t = (s - cum[i - 1]!) / segLen;
  const [ax, az] = pts[i - 1]!;
  const [bx, bz] = pts[i]!;
  const dx = bx - ax;
  const dz = bz - az;
  car.mesh.position.set(ax + dx * t, 1.2, az + dz * t);
  car.mesh.rotation.y = Math.atan2(-dz, dx);
}

function addMesh(
  group: Group,
  sink: MeshSink,
  material?: { transparent?: boolean; opacity?: number; map?: Texture },
): Mesh | null {
  if (sink.indices.length === 0) return null;
  const geometry = new BufferGeometry();
  geometry.setAttribute("position", new BufferAttribute(new Float32Array(sink.positions), 3));
  geometry.setAttribute("color", new BufferAttribute(new Float32Array(sink.colors), 3));
  if (material?.map) {
    geometry.setAttribute("uv", new BufferAttribute(new Float32Array(sink.uvs), 2));
  }
  geometry.setIndex(sink.indices);
  const mesh = new Mesh(
    geometry,
    new MeshBasicMaterial({
      vertexColors: true,
      side: DoubleSide,
      ...(material?.map ? { map: material.map } : {}),
      ...(material?.transparent
        ? { transparent: true, opacity: material.opacity ?? 1, depthWrite: false }
        : {}),
    }),
  );
  group.add(mesh);
  return mesh;
}

function addLines(group: Group, sink: LineSink): void {
  if (sink.positions.length === 0) return;
  const geometry = new BufferGeometry();
  geometry.setAttribute("position", new BufferAttribute(new Float32Array(sink.positions), 3));
  geometry.setAttribute("color", new BufferAttribute(new Float32Array(sink.colors), 3));
  group.add(new LineSegments(geometry, new LineBasicMaterial({ vertexColors: true })));
}

/** Pull every vertex of a ring toward its centroid by a fixed distance. */
function insetRing(
  pts: ReadonlyArray<[number, number]>,
  inset: number,
): Array<[number, number]> {
  let cx = 0;
  let cz = 0;
  for (const [x, z] of pts) {
    cx += x / pts.length;
    cz += z / pts.length;
  }
  return pts.map(([x, z]) => {
    const dx = cx - x;
    const dz = cz - z;
    const len = Math.hypot(dx, dz);
    if (len < inset * 2) return [x, z];
    return [x + (dx / len) * inset, z + (dz / len) * inset];
  });
}

/**
 * Interior partition walls around one room: cream faces with a white cap,
 * and a doorway gap cut into the room's longest wall.
 */
function partitionWalls(
  sink: MeshSink,
  pts: ReadonlyArray<[number, number]>,
  y0: number,
  y1: number,
  highlighted: boolean,
): void {
  const wall = highlighted ? P.shade(P.PARTITION_WALL, 1.06) : P.PARTITION_WALL;
  const wallDim = P.shade(wall, 0.86);

  // The doorway lives on the longest edge, centred.
  let longest = 0;
  let longestLen = 0;
  for (let i = 0; i < pts.length; i++) {
    const [ax, az] = pts[i]!;
    const [bx, bz] = pts[(i + 1) % pts.length]!;
    const len = Math.hypot(bx - ax, bz - az);
    if (len > longestLen) {
      longestLen = len;
      longest = i;
    }
  }

  const face = (ax: number, az: number, bx: number, bz: number, rgb: number): void => {
    const v0 = sink.vertex(ax, y0, az, rgb);
    const v1 = sink.vertex(bx, y0, bz, rgb);
    const v2 = sink.vertex(bx, y1, bz, rgb);
    const v3 = sink.vertex(ax, y1, az, rgb);
    sink.triangle(v0, v1, v2);
    sink.triangle(v0, v2, v3);
  };
  const cap = (ax: number, az: number, bx: number, bz: number): void => {
    let nx = bz - az;
    let nz = -(bx - ax);
    const len = Math.hypot(nx, nz) || 1;
    nx = (nx / len) * 0.09;
    nz = (nz / len) * 0.09;
    const v0 = sink.vertex(ax - nx, y1, az - nz, P.PARTITION_CAP);
    const v1 = sink.vertex(bx - nx, y1, bz - nz, P.PARTITION_CAP);
    const v2 = sink.vertex(bx + nx, y1, bz + nz, P.PARTITION_CAP);
    const v3 = sink.vertex(ax + nx, y1, az + nz, P.PARTITION_CAP);
    sink.triangle(v0, v1, v2);
    sink.triangle(v0, v2, v3);
  };

  for (let i = 0; i < pts.length; i++) {
    const [ax, az] = pts[i]!;
    const [bx, bz] = pts[(i + 1) % pts.length]!;
    const len = Math.hypot(bx - ax, bz - az);
    const rgb = i % 2 === 0 ? wall : wallDim;
    if (i === longest && len > 2.6) {
      // Split the wall around a 1.2 m doorway.
      const t0 = (len / 2 - 0.6) / len;
      const t1 = (len / 2 + 0.6) / len;
      const m0x = ax + (bx - ax) * t0;
      const m0z = az + (bz - az) * t0;
      const m1x = ax + (bx - ax) * t1;
      const m1z = az + (bz - az) * t1;
      face(ax, az, m0x, m0z, rgb);
      face(m1x, m1z, bx, bz, rgb);
      cap(ax, az, m0x, m0z);
      cap(m1x, m1z, bx, bz);
    } else {
      face(ax, az, bx, bz, rgb);
      cap(ax, az, bx, bz);
    }
  }
}

/** A dashed ring, 0.5 m dashes with 0.4 m gaps. */
function dashedRing(
  sink: LineSink,
  pts: ReadonlyArray<[number, number]>,
  y: number,
  rgb: number,
): void {
  const DASH = 0.5;
  const GAP = 0.4;
  for (let i = 0; i < pts.length; i++) {
    const [ax, az] = pts[i]!;
    const [bx, bz] = pts[(i + 1) % pts.length]!;
    const len = Math.hypot(bx - ax, bz - az);
    if (len < 0.05) continue;
    const dx = (bx - ax) / len;
    const dz = (bz - az) / len;
    for (let at = 0; at < len; at += DASH + GAP) {
      const end = Math.min(len, at + DASH);
      sink.segment(ax + dx * at, y, az + dz * at, ax + dx * end, y, az + dz * end, rgb);
    }
  }
}

/** Thin vertical skirt between two heights, for floor-plan slabs. */
function extrudeSlab(
  sink: MeshSink,
  ring: readonly Vec2[],
  proj: Projection,
  y0: number,
  y1: number,
  rgb: number,
): void {
  const pts = ring.map((p) => proj.toWorld(p));
  for (let i = 0; i < pts.length; i++) {
    const [ax, az] = pts[i]!;
    const [bx, bz] = pts[(i + 1) % pts.length]!;
    const v0 = sink.vertex(ax, y0, az, rgb);
    const v1 = sink.vertex(bx, y0, bz, rgb);
    const v2 = sink.vertex(bx, y1, bz, rgb);
    const v3 = sink.vertex(ax, y1, az, rgb);
    sink.triangle(v0, v1, v2);
    sink.triangle(v0, v2, v3);
  }
}

function disposeGroup(group: Group): void {
  for (const child of [...group.children]) {
    group.remove(child);
    if (child.userData.shared) {
      // Geometry/material are shared statics, but an InstancedMesh still owns
      // its per-instance buffers.
      if (child instanceof InstancedMesh) child.dispose();
      continue;
    }
    if (child instanceof Mesh || child instanceof LineSegments) {
      child.geometry.dispose();
      const material = child.material;
      if (Array.isArray(material)) material.forEach((m) => m.dispose());
      else material.dispose();
    } else if (child instanceof Group) {
      disposeGroup(child);
      continue;
    }
  }
}

/* ---------------- generated textures ---------------- */

/**
 * One facade cell: pale wall, dark window. Tinted per building by vertex
 * colour, so a single texture dresses every facade colour in the palette.
 */
function makeWindowTexture(anisotropy: number): Texture {
  const size = 128;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const g = canvas.getContext("2d")!;
  g.fillStyle = "#ffffff";
  g.fillRect(0, 0, size, size);
  // Floor slab shadow line across the cell bottom.
  g.fillStyle = "#e3e3e3";
  g.fillRect(0, size - 10, size, 10);
  // The window: dark pane in a slightly lighter frame.
  g.fillStyle = "#c9c9c9";
  g.fillRect(34, 30, 60, 62);
  g.fillStyle = "#2b333c";
  g.fillRect(38, 34, 52, 54);
  // A hint of sky reflection so panes read as glass, not holes.
  g.fillStyle = "rgba(255,255,255,0.18)";
  g.beginPath();
  g.moveTo(38, 34);
  g.lineTo(66, 34);
  g.lineTo(38, 70);
  g.closePath();
  g.fill();

  const texture = new CanvasTexture(canvas);
  texture.wrapS = RepeatWrapping;
  texture.wrapT = RepeatWrapping;
  texture.colorSpace = SRGBColorSpace;
  texture.anisotropy = anisotropy;
  return texture;
}

/** Blotchy grass, tiled over the ground plane. */
function makeGrassTexture(anisotropy: number): Texture {
  const size = 512;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const g = canvas.getContext("2d")!;
  g.fillStyle = cssHex(P.GRASS_BASE);
  g.fillRect(0, 0, size, size);

  // Deterministic blobs: the same field every load, and it tiles because
  // every blob is stamped at its wrapped neighbours too.
  let seed = 41;
  const rand = (): number => {
    seed = (seed * 1103515245 + 12345) & 0x7fffffff;
    return seed / 0x7fffffff;
  };
  for (let i = 0; i < 90; i++) {
    const x = rand() * size;
    const y = rand() * size;
    const r = 18 + rand() * 80;
    const light = rand() > 0.5;
    for (const [ox, oy] of [
      [0, 0],
      [size, 0],
      [-size, 0],
      [0, size],
      [0, -size],
    ] as const) {
      const grad = g.createRadialGradient(x + ox, y + oy, 0, x + ox, y + oy, r);
      const colour = light ? P.GRASS_LIGHT : P.GRASS_DARK;
      grad.addColorStop(0, cssRgba(colour, 0.28));
      grad.addColorStop(1, cssRgba(colour, 0));
      g.fillStyle = grad;
      g.fillRect(x + ox - r, y + oy - r, r * 2, r * 2);
    }
  }

  const texture = new CanvasTexture(canvas);
  texture.wrapS = RepeatWrapping;
  texture.wrapT = RepeatWrapping;
  texture.colorSpace = SRGBColorSpace;
  texture.anisotropy = anisotropy;
  return texture;
}

function cssHex(rgb: number): string {
  return `#${rgb.toString(16).padStart(6, "0")}`;
}

function cssRgba(rgb: number, a: number): string {
  return `rgba(${(rgb >> 16) & 0xff},${(rgb >> 8) & 0xff},${rgb & 0xff},${a})`;
}
